import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, FileText, Gavel, Loader2, Plus, ShieldAlert, Users } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Inquiries } from "@/components/common/Inquiries";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shortDate } from "@/lib/format";

const T_BLANK = { name: "", role: "trustee", appointed_on: new Date().toISOString().slice(0, 10), term_years: 3, email: "", status: "active", conflicts: [] };
const M_BLANK = { title: "", meeting_type: "board", meeting_date: new Date().toISOString().slice(0, 10), attendees: [], agenda: [], resolutions: [], minutes: "", status: "scheduled" };

export default function Governance() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [trustees, setTrustees] = useState(null);
  const detail = useRowDetail();
  const [meetings, setMeetings] = useState([]);
  const [tForm, setTForm] = useState(T_BLANK);
  const [mForm, setMForm] = useState(M_BLANK);
  const [agenda, setAgenda] = useState("");
  const [tOpen, setTOpen] = useState(false);
  const [mOpen, setMOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [t, m] = await Promise.all([api.get("/charity/trustees"), api.get("/charity/meetings")]);
    setTrustees(t.data);
    setMeetings(m.data);
  }, []);

  useEffect(() => { load().catch(() => setTrustees([])); }, [load]);

  const saveTrustee = async () => {
    setBusy("t");
    try {
      await api.post("/charity/trustees", tForm);
      toast.success("Trustee added to the governance directory");
      setTOpen(false);
      setTForm(T_BLANK);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const saveMeeting = async () => {
    setBusy("m");
    try {
      await api.post("/charity/meetings", {
        ...mForm, agenda: agenda.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Meeting scheduled");
      setMOpen(false);
      setMForm(M_BLANK);
      setAgenda("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!trustees) return <Loading label="Loading governance directory" />;
  const conflicts = trustees.flatMap((t) => (t.conflicts || []).map((c) => ({ ...c, trustee: t.name })));

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-023 · Governance Workspace"
        title="Trustees, meetings, resolutions and conflicts"
        description="Appointment and role history, board and AGM records with resolutions and minutes, plus conflict-of-interest declarations and their mitigations."
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-trustees-active" label="Active officers" accent="sky" icon={Users}
             value={trustees.filter((t) => t.status === "active").length} />
        <Kpi testId="kpi-trustees-total" label="Directory records" value={trustees.length} accent="slate" />
        <Kpi testId="kpi-meetings" label="Meetings recorded" value={meetings.length} accent="violet" icon={CalendarDays}
             sub={`${meetings.filter((m) => m.status === "scheduled").length} upcoming`} />
        <Kpi testId="kpi-conflicts" label="Conflict declarations" value={conflicts.length} accent="amber" icon={ShieldAlert} />
      </div>

      <Tabs defaultValue="trustees">
        <TabsList className="bg-muted/40">
          <TabsTrigger value="trustees" data-testid="tab-trustees">Trustee registry</TabsTrigger>
          <TabsTrigger value="meetings" data-testid="tab-meetings">Meetings &amp; resolutions</TabsTrigger>
          <TabsTrigger value="conflicts" data-testid="tab-conflicts">Conflicts of interest</TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries</TabsTrigger>
        </TabsList>

        <TabsContent value="inquiries" className="mt-4">
          <Inquiries />
        </TabsContent>

        <TabsContent value="trustees" className="mt-4">
          <Panel testId="panel-trustees" kicker="FR-GOV-001" title="Governance directory"
                 actions={can("governance", "write") && (
                   <Dialog open={tOpen} onOpenChange={setTOpen}>
                     <DialogTrigger asChild>
                       <Button size="sm" data-testid="new-trustee-button"
                               className="h-8 rounded-full bg-primary text-xs text-white hover:bg-primary/90">
                         <Plus className="mr-1 h-3 w-3" /> Add officer
                       </Button>
                     </DialogTrigger>
                     <DialogContent className="max-w-md border-border bg-card">
                       <DialogHeader><DialogTitle>Add trustee / officer</DialogTitle></DialogHeader>
                       <div className="grid grid-cols-2 gap-3">
                         <F label="Name" full>
                           <Input value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })}
                                  data-testid="trustee-name-input" className="bg-muted/40" />
                         </F>
                         <F label="Role">
                           <Select value={tForm.role} onValueChange={(v) => setTForm({ ...tForm, role: v })}>
                             <SelectTrigger data-testid="trustee-role-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                             <SelectContent>
                               {["chair", "trustee", "treasurer", "secretary", "director", "officer"].map((r) => (
                                 <SelectItem key={r} value={r}>{r}</SelectItem>
                               ))}
                             </SelectContent>
                           </Select>
                         </F>
                         <F label="Appointed on">
                           <Input type="date" value={tForm.appointed_on}
                                  onChange={(e) => setTForm({ ...tForm, appointed_on: e.target.value })}
                                  data-testid="trustee-appointed-input" className="bg-muted/40 font-mono" />
                         </F>
                         <F label="Email" full>
                           <Input value={tForm.email} onChange={(e) => setTForm({ ...tForm, email: e.target.value })}
                                  data-testid="trustee-email-input" className="bg-muted/40" />
                         </F>
                       </div>
                       <Button onClick={saveTrustee} disabled={busy === "t" || !tForm.name} data-testid="trustee-save-button"
                               className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         {busy === "t" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add officer
                       </Button>
                     </DialogContent>
                   </Dialog>
                 )}>
            <Table testId="trustees-table"
                   head={<><Th>Name</Th><Th>Role</Th><Th>Appointed</Th><Th>Term</Th><Th>Resigned</Th><Th>Status</Th></>}>
              {trustees.length === 0 && <Empty colSpan={6} />}
              {trustees.map((t) => (
                <tr key={refOf(t)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(t, refOf(t)))} data-testid={`trustee-row-${t.id}`}>
                  <Td>
                    <div className="font-medium text-foreground">{t.name}</div>
                    <div className="text-[11px] text-muted-foreground">{t.email || "—"}</div>
                  </Td>
                  <Td><Chip className="border-border bg-muted text-foreground/80">{t.role}</Chip></Td>
                  <Td className="font-mono text-[11px]">{shortDate(t.appointed_on)}</Td>
                  <Td className="font-mono text-[11px]">{t.term_years}y</Td>
                  <Td className="font-mono text-[11px]">{t.resigned_on ? shortDate(t.resigned_on) : "—"}</Td>
                  <Td>
                    <Chip className={t.status === "active"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : "border-border bg-muted text-muted-foreground"}>{t.status}</Chip>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="meetings" className="mt-4 space-y-4">
          <Panel testId="panel-meetings" kicker="FR-GOV-003" title="Board meetings & AGMs"
                 actions={can("governance", "write") && (
                   <Dialog open={mOpen} onOpenChange={setMOpen}>
                     <DialogTrigger asChild>
                       <Button size="sm" data-testid="new-meeting-button"
                               className="h-8 rounded-full bg-primary text-xs text-white hover:bg-primary/90">
                         <Plus className="mr-1 h-3 w-3" /> Schedule meeting
                       </Button>
                     </DialogTrigger>
                     <DialogContent className="max-w-md border-border bg-card">
                       <DialogHeader><DialogTitle>Schedule a meeting</DialogTitle></DialogHeader>
                       <div className="space-y-3">
                         <F label="Title">
                           <Input value={mForm.title} onChange={(e) => setMForm({ ...mForm, title: e.target.value })}
                                  data-testid="meeting-title-input" className="bg-muted/40" />
                         </F>
                         <F label="Type">
                           <Select value={mForm.meeting_type} onValueChange={(v) => setMForm({ ...mForm, meeting_type: v })}>
                             <SelectTrigger data-testid="meeting-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                             <SelectContent>
                               {["board", "agm", "committee", "egm"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                             </SelectContent>
                           </Select>
                         </F>
                         <F label="Date">
                           <Input type="date" value={mForm.meeting_date}
                                  onChange={(e) => setMForm({ ...mForm, meeting_date: e.target.value })}
                                  data-testid="meeting-date-input" className="bg-muted/40 font-mono" />
                         </F>
                         <F label="Agenda (one item per line)">
                           <Textarea rows={4} value={agenda} onChange={(e) => setAgenda(e.target.value)}
                                     data-testid="meeting-agenda-input" className="bg-muted/40 text-xs" />
                         </F>
                       </div>
                       <Button onClick={saveMeeting} disabled={busy === "m" || !mForm.title} data-testid="meeting-save-button"
                               className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         {busy === "m" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Schedule
                       </Button>
                     </DialogContent>
                   </Dialog>
                 )}>
            <div className="divide-y divide-border">
              {meetings.length === 0 && <div className="p-6 text-xs text-muted-foreground/70">No meetings recorded</div>}
              {meetings.map((m) => (
                <div key={m.id} className="p-4" data-testid={`meeting-row-${m.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <Gavel className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm font-semibold text-foreground">{m.title}</span>
                        <Chip className="border-border bg-muted text-foreground/80">{m.meeting_type}</Chip>
                        <Chip className={m.status === "completed"
                          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                          : "border-primary/40 bg-primary/10 text-primary"}>{m.status}</Chip>
                      </div>
                      <div className="mt-1 font-mono text-[11px] text-muted-foreground">{shortDate(m.meeting_date)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-muted-foreground/70">{(m.attendees || []).length} attendees</span>
                      <Button size="sm" variant="outline" data-testid={`board-pack-${m.id}`}
                              onClick={() => navigate(`/charity/governance/pack/${m.id}`)}
                              className="h-7 rounded-full border-border text-[11px]">
                        <FileText className="mr-1 h-3 w-3" /> Board pack
                      </Button>
                    </div>
                  </div>
                  {(m.agenda || []).length > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {m.agenda.map((a) => (
                        <li key={a} className="text-[11px] text-muted-foreground">· {a}</li>
                      ))}
                    </ul>
                  )}
                  {(m.resolutions || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.resolutions.map((r) => (
                        <span key={r.reference} className="rounded border border-border px-2 py-1 text-[10px] text-foreground/80">
                          <span className="font-mono text-primary">{r.reference}</span> {r.text} · {r.outcome}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="conflicts" className="mt-4">
          <Panel testId="panel-conflicts" kicker="FR-GOV-002" title="Conflicts & related parties">
            <Table testId="conflicts-table"
                   head={<><Th>Officer</Th><Th>Declared</Th><Th>Nature</Th><Th>Mitigation</Th><Th>Status</Th></>}>
              {conflicts.length === 0 && <Empty colSpan={5} label="No declarations recorded" />}
              {conflicts.map((c, i) => (
                <tr key={refOf(i)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(i, refOf(i)))} data-testid={`conflict-row-${i}`}>
                  <Td className="font-medium text-foreground">{c.trustee}</Td>
                  <Td className="font-mono text-[11px]">{shortDate(c.declared_on)}</Td>
                  <Td className="text-muted-foreground">{c.nature}</Td>
                  <Td className="text-muted-foreground">{c.mitigation}</Td>
                  <Td><Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">{c.status}</Chip></Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>
      </Tabs>

      {detail.sheet}
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);
